// @greenlight-spec: 59-platform-agent.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Send, Square } from 'lucide-react';
import { toast } from 'sonner';

import { usePageTitle } from '@/components/app-shell/AppShell';
import {
  LiveStepLine,
  TranscriptRow,
  stepLabel,
  useCycle,
  useStreamedTranscript,
  type TranscriptEvent,
} from '@/components/agents/Transcript';
import { EmptyState } from '@/components/greenlight/EmptyState';
import { LiveIndicator } from '@/components/greenlight/LiveIndicator';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { isPreviewFeatureEnabled } from '@/lib/preview-features';

import { ACTION_REQUESTS, type SessionDef } from './fixtures';
import {
  ActionRequestCard,
  Ago,
  RoadmapTag,
  SessionStatusBadge,
  TriggerChip,
  agentById,
  resolveSession,
  sessionById,
  tokens,
  usd,
} from './shared';

export function SessionPage(): React.JSX.Element {
  const { agentId = '', sessionId = '' } = useParams<{ agentId: string; sessionId: string }>();
  const agent = agentById(agentId);
  const session = sessionById(sessionId);
  const [params] = useSearchParams();
  const view = session ? resolveSession(session, params.get('target')) : null;
  usePageTitle(view?.title ?? 'Session');
  const [draft, setDraft] = React.useState('');
  const [extra, setExtra] = React.useState<TranscriptEvent[]>([]);

  if (!isPreviewFeatureEnabled('greenlight-agents')) return <Navigate to="/apps" replace />;
  if (!agent || !session || !view) return <EmptyState title="No such session" />;

  const live = session.status === 'running';
  const send = () => {
    const text = draft.trim();
    if (!text) return;
    setExtra((e) => [
      ...e,
      { id: `u${e.length}`, kind: 'user', at: new Date().toISOString(), text },
      {
        id: `a${e.length}`,
        kind: 'assistant',
        at: new Date().toISOString(),
        text: 'Resuming this session with your follow-up…',
      },
    ]);
    setDraft('');
  };

  return (
    <div className="space-y-4">
      <Link
        to={`/agents/${agent.id}`}
        className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-3.5" aria-hidden /> {agent.name}
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-xl font-semibold leading-tight tracking-[-0.02em]">
            {view.title}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
            <SessionStatusBadge status={session.status} />
            {agent.status === 'roadmap' && <RoadmapTag />}
            <TriggerChip kind={view.trigger.kind} by={view.trigger.by} />
            <span>
              started <Ago iso={session.startedAt} />
            </span>
            {live && <LiveIndicator />}
          </div>
        </div>
        {live && (
          <Button
            variant="destructive-outline"
            size="sm"
            onClick={() => toast.success('Stop requested')}
          >
            <Square className="mr-1.5 size-3.5" aria-hidden /> Stop
          </Button>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-h-[520px] flex-col rounded-gl-lg border border-border bg-card">
          <LiveTranscript
            key={`${session.id}-${view.target}`}
            session={session}
            events={view.transcript}
            extra={extra}
          />
          <div className="border-t border-border p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={
                  session.status === 'done' || session.status === 'failed'
                    ? 'Continue this session…'
                    : 'Send a follow-up…'
                }
                rows={2}
                className="min-h-[44px] resize-none text-[13.5px]"
                aria-label="Follow-up message"
              />
              <Button size="icon" onClick={send} aria-label="Send">
                <Send className="size-4" aria-hidden />
              </Button>
            </div>
            <p className="mt-1.5 text-[11.5px] text-muted-foreground">
              A follow-up on an ended session resumes it from its saved state.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {session.report && (
            <SectionCard title="Report" headerBar>
              <div className="space-y-3 px-4 py-3 text-[13px]">
                <div className="font-medium">{session.report.verdict}</div>
                {(['findings', 'actions', 'asks'] as const).map(
                  (k) =>
                    session.report![k].length > 0 && (
                      <div key={k}>
                        <div className="text-[12px] capitalize text-muted-foreground">
                          {k === 'asks' ? 'Waiting on you' : k}
                        </div>
                        <ul className="list-disc pl-4">
                          {session.report![k].map((x) => (
                            <li key={x}>{x}</li>
                          ))}
                        </ul>
                      </div>
                    ),
                )}
              </div>
            </SectionCard>
          )}
          <SectionCard title="Usage" headerBar>
            <dl className="grid grid-cols-2 gap-px bg-border">
              {[
                ['Tokens', tokens(session.tokens)],
                ['Cost', usd(session.costUsd)],
                ['Adapter', 'Claude Agent SDK'],
                ['Source', 'Foundry · acme-ai-prod'],
              ].map(([l, v]) => (
                <div key={l} className="bg-card px-4 py-3">
                  <dt className="text-[12px] text-muted-foreground">{l}</dt>
                  <dd className="truncate font-mono text-[13px]">{v}</dd>
                </div>
              ))}
            </dl>
          </SectionCard>
          {session.target && (
            <SectionCard title="Links" headerBar>
              <ul className="px-4 py-2 text-[13px]">
                <li className="py-1">
                  <span className="text-muted-foreground">Target · </span>
                  <span className="text-link">{view.target}</span>
                </li>
                <li className="py-1">
                  <span className="text-muted-foreground">Audit · </span>
                  <span className="text-link">platform_agent.session {session.id}</span>
                </li>
              </ul>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}

function LiveTranscript({
  session,
  events,
  extra,
}: {
  session: SessionDef;
  events: TranscriptEvent[];
  extra: TranscriptEvent[];
}) {
  const live = session.status === 'running';
  const stream = useStreamedTranscript(events, {
    start: live ? Math.min(3, events.length) : events.length,
    stepMs: 1400,
  });
  const cycled = useCycle(session.liveSteps ?? [], 3000);
  const endRef = React.useRef<HTMLLIElement>(null);
  React.useEffect(() => {
    if (live) endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [live, stream.visible.length, stream.streamedChars]);
  const shown = [...stream.visible, ...extra];
  const lastId = stream.visible[stream.visible.length - 1]?.id;
  return (
    <ol
      className="flex-1 space-y-3 overflow-y-auto p-4"
      aria-label="Transcript"
      aria-live={live ? 'polite' : undefined}
    >
      {shown.length === 0 && (
        <li className="text-[13px] text-muted-foreground">
          Transcript not in the fixture set for this session.
        </li>
      )}
      {shown.map((e) => (
        <li key={e.id}>
          <TranscriptRow
            event={e}
            {...(e.id === lastId && stream.streamedChars !== undefined
              ? { streamedChars: stream.streamedChars }
              : {})}
            renderAction={(requestId) => {
              const req = ACTION_REQUESTS.find((r) => r.id === requestId);
              return req ? (
                <ActionRequestCard
                  request={req}
                  compact
                  onDecide={() => toast.success('Decided')}
                />
              ) : null;
            }}
          />
        </li>
      ))}
      {live && (
        <li ref={endRef}>
          <LiveStepLine
            text={stream.done ? (cycled ?? 'Working…') : stepLabel(stream.next)}
            className="text-muted-foreground"
          />
        </li>
      )}
    </ol>
  );
}
